import * as RadixCheckbox from '@radix-ui/react-checkbox'
import { cx } from 'class-variance-authority'
import type { ComponentPropsWithoutRef } from 'react'
import { forwardRef, useState } from 'react'

import styles from './Checkbox.module.scss'
export interface CheckboxProps
  extends Omit<
    ComponentPropsWithoutRef<typeof RadixCheckbox.Root>,
    'children'
  > {
  label: string
  className?: string
  warning?: boolean
}

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ label, className, warning = false, ...props }, ref) => {
    const [internalChecked, setInternalChecked] =
      useState<RadixCheckbox.CheckedState>(props.defaultChecked ?? false)
    const checkedValue =
      props.checked !== undefined ? props.checked : internalChecked

    const handleCheckedChange = (newValue: RadixCheckbox.CheckedState) => {
      if (props.checked === undefined) {
        setInternalChecked(newValue)
      }
      props.onCheckedChange?.(newValue)
    }

    return (
      <label className={cx(styles.wrapper, className)} data-warning={warning}>
        <RadixCheckbox.Root
          {...props}
          checked={checkedValue}
          className={styles.root}
          data-warning={warning}
          onCheckedChange={handleCheckedChange}
          ref={ref}
        >
          <RadixCheckbox.Indicator className={styles.indicator}>
            {checkedValue === 'indeterminate' ? (
              <svg
                fill="none"
                height="16"
                viewBox="0 0 16 16"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  fill="currentColor"
                  height="1"
                  rx="0.5"
                  width="8"
                  x="4"
                  y="7.5"
                />
              </svg>
            ) : checkedValue === true ? (
              <svg
                fill="none"
                height="16"
                viewBox="0 0 16 16"
                width="16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.64936 10.1053L12.2994 4.45531C12.4327 4.32198 12.5882 4.25531 12.766 4.25531C12.9438 4.25531 13.0994 4.32198 13.2327 4.45531C13.366 4.58864 13.4327 4.74698 13.4327 4.93031C13.4327 5.11364 13.366 5.27198 13.2327 5.40531L7.11602 11.5386C6.98269 11.672 6.82713 11.7386 6.64936 11.7386C6.47158 11.7386 6.31602 11.672 6.18269 11.5386L3.31602 8.67198C3.18269 8.53864 3.1188 8.38031 3.12436 8.19698C3.12991 8.01364 3.19936 7.85531 3.33269 7.72198C3.46602 7.58864 3.62436 7.52198 3.80769 7.52198C3.99102 7.52198 4.14936 7.58864 4.28269 7.72198L6.64936 10.1053Z"
                  fill="currentColor"
                />
              </svg>
            ) : null}
          </RadixCheckbox.Indicator>
        </RadixCheckbox.Root>

        <span className={styles.labelText}>{label}</span>
      </label>
    )
  }
)

Checkbox.displayName = 'Checkbox'
